import { type TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { BrandXS, LabelM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MISSIONS_CATEGORY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import { MissionToastBackground } from '../MissionToastBackground';
import type { DailyBoostToastContent_task } from './__generated__/index.graphql';

const ProgressWrapper = styled(Horizontal)`
  flex: 1;
  max-width: 50%;
`;

type Props = { task: DailyBoostToastContent_task };
export const DailyBoostToastContent = ({ task }: Props) => {
  const { parentTask, progress, target, title } = task;
  const { generateSportContextPath } = useSportContext();
  const [showClaimButton, setShowClaimButton] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onAnimationEnd = () => {
    if (progress >= target) {
      timeoutRef.current = setTimeout(() => {
        setShowClaimButton(true);
      }, 500);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Link
      to={`${generateSportContextPath(ANY_SPORT_MISSIONS_CATEGORY_RARITY, {
        params: {
          rarity: Rarity.common.toLowerCase(),
          category: 'play',
        },
      })}`}
    >
      <MissionToastBackground color="#7029FF" showHalo>
        <SBHorizontal>
          <Vertical>
            <BrandXS color="var(--c-nd-600)">{title}</BrandXS>
            <LabelM bold>{parentTask?.title}</LabelM>
          </Vertical>
          {showClaimButton ? (
            <Button color="primary" size="compact" asSpan>
              <FormattedMessage {...glossary.claim} />
            </Button>
          ) : (
            <ProgressWrapper gap={1.5}>
              <Progress
                value={progress}
                max={target}
                onAnimationEnd={onAnimationEnd}
                color="linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)"
              />
              <Horizontal gap={0}>
                <LabelM bold>{progress}</LabelM>
                <LabelM>&nbsp;/&nbsp;{target}</LabelM>
              </Horizontal>
            </ProgressWrapper>
          )}
        </SBHorizontal>
      </MissionToastBackground>
    </Link>
  );
};

DailyBoostToastContent.fragments = {
  task: gql`
    fragment DailyBoostToastContent_task on Task {
      id
      title
      progress
      target
      parentTask {
        id
        title
      }
    }
  ` as TypedDocumentNode<DailyBoostToastContent_task>,
};
